import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  Download,
  Printer,
  Home,
  MessageCircle,
  Sparkles,
  ShoppingBag,
  ChefHat,
  Receipt,
  UtensilsCrossed,
} from "lucide-react";
import { toast } from "sonner";
import { orderApi } from "../lib/orderApi";
import { businessInfo } from "../data/business";

const STATUS_CONFIG = {
  placed: { label: "Order Placed", color: "bg-blue-100 text-blue-800 border-blue-200", icon: Clock },
  accepted: { label: "Accepted by Kitchen", color: "bg-amber-100 text-amber-800 border-amber-200", icon: ChefHat },
  preparing: { label: "Preparing Fresh", color: "bg-orange-100 text-orange-800 border-orange-200", icon: Sparkles },
  ready: { label: "Ready to Serve", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  served: { label: "Served 🎉", color: "bg-stone-100 text-stone-800 border-stone-200", icon: CheckCircle2 },
};

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let intervalId;

    const fetchOrderDetails = async () => {
      try {
        const data = await orderApi.getOrder(orderId);
        setOrder(data);
      } catch (err) {
        console.warn("Could not fetch order from backend:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
    intervalId = setInterval(fetchOrderDetails, 8000);
    return () => clearInterval(intervalId);
  }, [orderId]);

  const handleDownloadInvoice = async () => {
    try {
      const blob = await orderApi.downloadInvoiceBlob(orderId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `HimCream_Invoice_${order?.order_number || orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error("Invoice is generating. You can view or print in the café.");
    }
  };

  const handlePrintInvoice = () => {
    window.open(orderApi.getInvoiceUrl(orderId), "_blank");
  };

  const handleShareWhatsApp = () => {
    const text = `*HimCream Naturals Order #${order?.order_number || orderId}*%0A` +
      `Table: ${order?.table_name || "Dine-in"}%0A` +
      `Total: ₹${order?.total || 0}%0A` +
      `Status: ${order?.status || "Placed"}%0A` +
      `Address: ${businessInfo.address.full}`;
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF9] max-w-md mx-auto p-6 flex flex-col items-center justify-center space-y-4">
        <div className="w-10 h-10 border-3 border-sky-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm font-semibold text-stone-600">
          Loading your order confirmation...
        </p>
      </div>
    );
  }

  const currentStatus = order?.status
    ? STATUS_CONFIG[order.status] || STATUS_CONFIG.placed
    : STATUS_CONFIG.placed;

  return (
    <div className="min-h-screen bg-[#FFFDF9] pb-24 max-w-lg mx-auto p-4 space-y-5">
      {/* SUCCESS HERO */}
      <div className="bg-white rounded-3xl p-6 border border-stone-200/80 shadow-xs text-center space-y-3">
        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-xs">
          <CheckCircle2 className="w-9 h-9" />
        </div>

        <div>
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
            Order Placed Successfully
          </span>
          <h1 className="text-2xl font-extrabold text-[#2C3E50] mt-2">
            Thank You, {order?.customer_name || "Valued Guest"}!
          </h1>
          <p className="text-xs text-stone-500 mt-1">
            Order #{order?.order_number || orderId?.slice(0, 8)} • Table {order?.table_name || "Dine-in"}
          </p>
        </div>

        {/* STATUS PILL */}
        <div className="pt-2">
          <div
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold border ${currentStatus.color}`}
          >
            <currentStatus.icon className="w-3.5 h-3.5" />
            <span>{currentStatus.label}</span>
          </div>
        </div>
      </div>

      {/* ORDER SUMMARY */}
      <div className="bg-white rounded-3xl p-5 border border-stone-200/80 shadow-xs space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-stone-100">
          <h2 className="font-bold text-sm text-[#2C3E50] flex items-center gap-1.5">
            <Receipt className="w-4 h-4 text-sky-600" />
            <span>Order Summary</span>
          </h2>
          <span className="text-xs text-stone-400 font-mono">
            {order?.created_at
              ? new Date(order.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "Just now"}
          </span>
        </div>

        {/* ITEMS */}
        <div className="space-y-2 text-xs sm:text-sm">
          {order?.items?.map((item, idx) => (
            <div key={idx} className="flex justify-between items-center text-stone-700">
              <span>
                {item.product_name} <strong className="text-stone-900">× {item.quantity}</strong>
              </span>
              <span className="font-semibold text-stone-900">
                ₹{(Number(item.price) * item.quantity).toFixed(2)}
              </span>
            </div>
          ))}
        </div>

        {/* TOTAL */}
        <div className="border-t border-stone-100 pt-3 flex justify-between items-center font-extrabold text-base text-[#2C3E50]">
          <span>Total Amount</span>
          <span className="text-sky-600">₹{Number(order?.total || 0).toFixed(2)}</span>
        </div>

        {/* PAYMENT INFO */}
        <div className="pt-2 text-xs text-stone-500 flex items-center justify-between">
          <span>Payment: <strong className="text-stone-700 uppercase">{order?.payment_method === "pay_now" ? "UPI QR" : "Counter"}</strong></span>
          <span className="capitalize px-2 py-0.5 rounded bg-stone-100 text-stone-700 font-semibold">
            {order?.payment_status || "Pending"}
          </span>
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <div className="grid grid-cols-3 gap-2.5">
        <button
          onClick={handleDownloadInvoice}
          className="p-3 bg-white hover:bg-stone-50 rounded-2xl border border-stone-200 text-[#2C3E50] text-xs font-bold flex flex-col items-center justify-center gap-1 shadow-xs transition"
        >
          <Download className="w-4 h-4 text-sky-600" />
          <span>Invoice</span>
        </button>

        <button
          onClick={handlePrintInvoice}
          className="p-3 bg-white hover:bg-stone-50 rounded-2xl border border-stone-200 text-[#2C3E50] text-xs font-bold flex flex-col items-center justify-center gap-1 shadow-xs transition"
        >
          <Printer className="w-4 h-4 text-purple-600" />
          <span>Print</span>
        </button>

        <button
          onClick={handleShareWhatsApp}
          className="p-3 bg-white hover:bg-stone-50 rounded-2xl border border-stone-200 text-[#2C3E50] text-xs font-bold flex flex-col items-center justify-center gap-1 shadow-xs transition"
        >
          <MessageCircle className="w-4 h-4 text-green-600" />
          <span>Share</span>
        </button>
      </div>

      {/* RETURN HOME / NEW ORDER BUTTONS */}
      <div className="pt-2 space-y-2">
        <Link
          to="/"
          className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm py-3.5 rounded-2xl shadow flex items-center justify-center gap-2 transition"
        >
          <Home className="w-4 h-4" />
          <span>Return to Homepage</span>
        </Link>

        <Link
          to="/select-table"
          className="w-full bg-stone-100 hover:bg-stone-200 text-stone-700 font-semibold text-xs py-3 rounded-2xl flex items-center justify-center gap-1.5 transition"
        >
          <UtensilsCrossed className="w-3.5 h-3.5" />
          <span>Place Another Order</span>
        </Link>
      </div>
    </div>
  );
}
